import React from 'react';
import Context from '../Utilities';

function OpposingQuotes() {
    const types = [
        {
            // id is the type being described. Description keys refer to the user's perspective
            id: 'ApproveShare',
            name: 'Approves of Trump, would share',
            description: {
                // if you are one of the types below, this is the text you see about ApproveShares
                DisapproveShare:
                    'People who approve of Trump and say they would share their opinions about the president are motivated by their strong <em>positive</em> feelings about him and the importance of sharing their views with Trump disapprovers. Here are some of these Trump approvers explaining why they would share their views at a dinner with people who really dislike Trump:',
                DisapproveNotShare:
                    'People have different views of Trump, and they are divided in their willingness to share their opinions of him. Among those who approve of the president, a majority are comfortable expressing their feelings about him. Many approvers who would share say it is important to let others know their point of view. Others say they feel compelled to speak about their support of Trump. Here are some examples of what they said:',
                ApproveNotShare:
                    'Many people who approve of Trump <em>would</em> speak up around those who really dislike him. Those approvers who would share their views most often cite the importance of others knowing their positions on Trump and their own positive feelings toward him as the main reasons why they would share their opinions. Here are some examples of what they said:',
            },
            quote1:
                'I think too many people talk to their friends and family only about political matters. I would share my thoughts and hope for a dialogue that was informative and respectful.”',
            quote1Attribution: '– Woman, 72',
            quote2:
                'I voted for him. I believe in him and I won’t let people bash him. I will stick up for him. He is a very misunderstood man.”',
            quote2Attribution: '– Man, 47',
            quote3:
                'I would like to get leftists to think more, not just repeat what the left-leaning media tells them.”',
            quote3Attribution: '– Man, 52',
        },
        {
            id: 'ApproveNotShare',
            name: 'Approves of Trump, would not share',
            description: {
                // if you are one of the types below, this is the text you see about ApproveNotShares
                DisapproveShare:
                    'Those who approve of Trump and say they would <em>not</em> share their opinions about the president are mostly motivated by their desire to avoid conflict, while fewer are concerned they would be judged for their views. Here is how they explain why they would not share their views at a dinner with people who really dislike Trump:',
                DisapproveNotShare:
                    'Others who approve of Trump say they would <em>not</em> share their opinions. They are mostly motivated by their desire to avoid conflict, though some are concerned they would be judged for their views. Here is how they explain why they would not share their views at a dinner with people who really dislike Trump:',
                ApproveShare:
                    'Many people who approve of Trump would <em>not</em> speak up around those who really dislike him. Those approvers who would not share their views most often cite wanting to avoid conflict as the main reason why they would not share their opinions. Here are some examples of what they said:',
            },
            quote1:
                'In general, my policy is not to discuss politics publicly. A conservative will not change a liberal’s mind, and a liberal won’t change a conservative’s mind. So, it’s pointless.”',
            quote1Attribution: '– Man, 51',
            quote2:
                'Opinions are not worth much and I frequently avoid sharing my opinion on many topics in order to avoid being misunderstood. People are quick to label and judge and thus, not understand others accurately.”',
            quote2Attribution: '– Man, 54',
            quote3:
                'Because I don’t like being confrontational with people. Especially people I don’t know well.”',
            quote3Attribution: '– Woman, 54',
        },
        {
            id: 'DisapproveShare',
            name: 'Dispproves of Trump, would share',
            description: {
                // if you are one of the types below, this is the text you see about DisapproveShares
                DisapproveNotShare:
                    'And then there are people who disapprove of Trump and <em>would</em> share their feelings about him. Like Trump approvers who are comfortable expressing their views, they want people to know where they stand on the president. Here is what they have to say:',
                ApproveNotShare:
                    'People who <em>disapprove</em> of Trump and say they would share their opinions about the president also are motivated by their strong feelings about him – in this case, negative feelings – and the importance of sharing their views with Trump approvers. Here are some of these Trump disapprovers explaining why they would share their views at dinner with people who really like Trump:',
                ApproveShare:
                    'There are others who <em>disapprove</em> of Trump and would share their opinions of him. Many have strongly negative feelings about Trump or say they feel compelled to share their views with those who have a differing point of view. Here’s what they had to say:',
            },
            quote1:
                'Trump is so uniquely awful that it’s important that anyone who likes him hears from those who don’t, no matter how painful or socially awkward that may be.”',
            quote1Attribution: '– Man, 44',
            quote2:
                'People who feel positively towards Donald Trump are not people I care to pacify or be around quite honestly. If they choose to share their views, I’d quite kindly share mine and leave afterward.”',
            quote2Attribution: '– Woman, 23',
            quote3:
                'We live in a democracy and everybody is entitled to their opinion about elected officials. Just because we don’t agree on things doesn’t mean that we shouldn’t share our different views with each other. Understanding and hearing each other is better than avoiding or lying to each other.”',
            quote3Attribution: '– Woman, 41',
        },
        {
            id: 'DisapproveNotShare',
            name: 'Disapproves of Trump, would not share',
            description: {
                // if you are one of the types below, this is the text you see about DisapproveNotShares
                DisapproveShare:
                    'And then there are people who disapprove of Trump and would <em>not</em> share their feelings about the president. Most in this group also are reluctant to express their opinions because they would prefer to avoid conflict. Here is what they have to say:',
                ApproveNotShare:
                    'And then there are those Trump disapprovers who would feel uncomfortable sharing their opinions among people who like him. Here’s what they had to say:',
                ApproveShare:
                    'People who disapprove of Trump and say they would <em>not</em> share their opinions about the president are motivated by similar factors as those who approve of Trump and are reluctant to express their opinions. Here are some of these Trump disapprovers explaining why they would not share their views at a dinner with people who really like Trump:',
            },
            quote1:
                'Because it’s not my job to change anyone. Engaging in a debate with someone who did not ask to debate, will only cause tension. I’m secure enough to smile, nod and sip my drink. I don’t need anyone to agree with me.”',
            quote1Attribution: '– Woman, 38',
            quote2:
                'I do not like confrontation. I will express my views of Donald Trump at the ballot box by voting against him.”',
            quote2Attribution: '– Man, 70',
            quote3:
                'I was raised not to talk about politics, religion or money with people I’m not close to. While I don’t adhere to that all of the time, expressing an opposing viewpoint while surrounded by strangers would probably push me outside of my comfort zone.”',
            quote3Attribution: '– Man, 28',
        },
    ];

    function createMarkup(thisType, type) {
        return {
            __html: thisType.description[type],
        };
    }

    return (
        <Context.Consumer>
            {({ type }) => {
                const typesRows = types
                    .filter(function(thisType) {
                        if (thisType.id === type) {
                            return false; // skip
                        }
                        return true;
                    })
                    .map(thisType => (
                        <div className="quote">
                            <div className="typeDescription">
                                <img
                                    src={`https://assets.pewresearch.org/interactives/politics/2019/dinner-party/Path_${type}/Path_${type}_${thisType.id}.png`}
                                    alt={`Illustration of a fellow dinner party guest, who ${thisType.name}`}
                                />
                                <p>
                                    <div
                                        dangerouslySetInnerHTML={createMarkup(
                                            thisType,
                                            type,
                                        )}
                                    />
                                </p>
                            </div>
                            <div className={`quote1 quoteTypes ${thisType.id}`}>
                                <p className={`${thisType.id}`}>
                                    {thisType.quote1}
                                    <br />
                                    <span
                                        className={`attribution ${thisType.id}`}
                                    >
                                        {thisType.quote1Attribution}
                                    </span>
                                </p>
                            </div>
                            <div className={`quote2 quoteTypes ${thisType.id}`}>
                                <p className={`${thisType.id}`}>
                                    {thisType.quote2}
                                    <br />
                                    <span
                                        className={`attribution ${thisType.id}`}
                                    >
                                        {thisType.quote2Attribution}
                                    </span>
                                </p>

                                <img
                                    src={`https://assets.pewresearch.org/interactives/politics/2019/dinner-party/Path_${thisType.id}/QuoteIllustrations.png`}
                                    alt="Spot illustration of guests talking"
                                />
                            </div>
                            <div className={`quote3 quoteTypes ${thisType.id}`}>
                                <p className={`${thisType.id}`}>
                                    {thisType.quote3}
                                    <br />
                                    <span
                                        className={`attribution ${thisType.id}`}
                                    >
                                        {thisType.quote3Attribution}
                                    </span>
                                </p>
                            </div>
                        </div>
                    ));
                return <div id="OpposingQuotes">{typesRows}</div>;
            }}
        </Context.Consumer>
    );
}

export default OpposingQuotes;
